import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Ban, PhoneCall, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { PEOPLE, spaceOf } from "@/lib/spaces";
import { fromNow } from "@/lib/time";
import { Btn, SalesHeader, StageBadge, WarmthBadge } from "@/components/sales/ui";
import { STAGES } from "@/components/sales/constants";
import { AddCompanyDialog } from "@/components/sales/AddCompanyDialog";

const Sel = ({ value, onChange, children, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-10 rounded-full border bg-card px-3 text-sm">{children}</select>
);

export default function Companies() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [text, setText] = useState("");
  const [f, setF] = useState({ q: "", stage: sp.get("stage") || "", owner: sp.get("owner") === "me" ? user.username : "", warmth: sp.get("warmth") || "",
    followup: sp.get("followup") || "", industry: "", insight_only: sp.get("insight") === "1" });
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setF((s) => ({ ...s, q: text })), 300);
    return () => clearTimeout(t);
  }, [text]);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const items = useFetch("/sales/companies", params);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="companies-page">
      <SalesHeader title="Companies" subtitle="Search by name, website, postcode or phone number.">
        <Btn variant="primary" onClick={() => setAdding(true)} data-testid="add-company-btn"><Plus className="h-4 w-4" /> Add Company</Btn>
      </SalesHeader>
      <div className="mb-5 flex flex-wrap gap-2">
        <div className="relative min-w-[260px] flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search companies…" className="h-10 rounded-full pl-10" data-testid="companies-search-input" />
        </div>
        <Sel value={f.stage} onChange={set("stage")} testid="filter-stage"><option value="">All stages</option>{STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</Sel>
        <Sel value={f.owner} onChange={set("owner")} testid="filter-owner"><option value="">Anyone</option><option value="unassigned">Unassigned</option>{PEOPLE.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}</Sel>
        <Sel value={f.warmth} onChange={set("warmth")} testid="filter-warmth"><option value="">Any warmth</option>{["Hot", "Warm", "Potential", "Cold", "Very cold", "Insufficient engagement"].map((w) => <option key={w}>{w}</option>)}</Sel>
        <Sel value={f.followup} onChange={set("followup")} testid="filter-followup"><option value="">Any follow-up</option><option value="due">Follow-up due</option></Sel>
        <Input value={f.industry} onChange={(e) => set("industry")(e.target.value)} placeholder="Industry" className="h-10 w-36 rounded-full" data-testid="filter-industry" />
        <label className="flex items-center gap-2 rounded-full border px-3 text-sm"><input type="checkbox" checked={f.insight_only} onChange={(e) => set("insight_only")(e.target.checked)} data-testid="filter-insight" /> Has Insight calls</label>
      </div>
      {!items ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.04]" /> : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed p-10 text-sm text-muted-foreground" data-testid="companies-empty">No companies match. Try another search, or import leads.</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border" data-testid="companies-list">
          {items.map((c) => (
            <Link key={c.id} to={`/sales/companies/${c.id}`} data-testid={`company-row-${c.id}`} className="flex flex-wrap items-center gap-3 border-b bg-card/60 px-5 py-3.5 transition-colors last:border-0 hover:bg-white/[0.04]">
              <div className="min-w-[200px] flex-1">
                <p className="flex items-center gap-2 font-semibold">{c.name}{c.do_not_contact && <Ban className="h-3.5 w-3.5 text-red-400" />}</p>
                <p className="text-xs text-muted-foreground">{[c.industry, c.postcode, c.phones?.[0]?.display].filter(Boolean).join(" · ") || "No details yet"}</p>
              </div>
              {c.insight_call_count > 0 && <span className="flex items-center gap-1 text-xs text-emerald-300"><PhoneCall className="h-3 w-3" /> {c.insight_call_count}</span>}
              <StageBadge stage={c.stage} /><WarmthBadge w={c.warmth} />
              <span className="w-20 text-xs text-muted-foreground">{c.owner ? spaceOf(c.owner).name : "Unassigned"}</span>
              <span className="hidden w-24 text-right text-xs text-muted-foreground md:block">{c.last_contacted_at ? fromNow(c.last_contacted_at) : "Not contacted"}</span>
            </Link>
          ))}
        </div>
      )}
      <AddCompanyDialog open={adding} onClose={() => setAdding(false)} onCreated={(c) => nav(`/sales/companies/${c.id}`)} />
    </div>
  );
}
